/**
 * Product analytics helpers: admin program lifecycle (column transitions) and shared context.
 */
import { eventTracking, events, attributeKeys } from './amplitude';
import { store } from '../redux/store';
import { finalizeAimdBuildTime } from '../pages/new-designs/tabs/aimd-v2/hooks/useAimdHeartbeat';

type OnboardingReduxSnapshot = {
  userProfile: { data?: { _id?: string; personal_details?: { email?: string; category?: string } } };
  selectedGroup: { data?: { name?: string; role?: string } };
  orgDetails: { data?: { name?: string; metadata?: { orgType?: string } } };
};

const ADMIN_PROGRAM_STATUS_STORAGE = 'amp_admin_program_column_v1';

type ColumnKey = 'design' | 'published' | 'invited' | 'active';

const COLUMN_TO_EVENT: Record<ColumnKey, string> = {
  design: events.programInDesign,
  published: events.programPublished,
  invited: events.programInvited,
  active: events.programActive
};

const COLUMN_TO_ADMIN_STATUS: Record<ColumnKey, string> = {
  design: 'In Design',
  published: 'Published',
  invited: 'Invited',
  active: 'Active'
};

function readStoredColumns(): Record<string, ColumnKey> {
  if (typeof sessionStorage === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(ADMIN_PROGRAM_STATUS_STORAGE);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    const out: Record<string, ColumnKey> = {};
    Object.entries(parsed).forEach(([k, v]) => {
      if (v === 'design' || v === 'published' || v === 'invited' || v === 'active') {
        out[k] = v;
      }
    });
    return out;
  } catch {
    return {};
  }
}

function writeStoredColumns(map: Record<string, ColumnKey>) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(ADMIN_PROGRAM_STATUS_STORAGE, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export type AdminProgramRow = {
  grpId: string;
  name: string;
  status: string;
  type?: string;
  typeLabel?: string;
  /** BE-accumulated active build seconds (ATC). Keeps growing until the program
   *  is invited — only used as a fallback for programs created before the
   *  "Create"-click freeze shipped. */
  aimdBuildSeconds?: number;
  /** ATC frozen at the "Create" click — the value Amplitude reports. */
  aimdCreateSeconds?: number | null;
};

export type AmplitudeOrgContext = {
  organizationName?: string;
  organizationType?: string;
  userEmail?: string;
  userRole?: string;
};

/**
 * Map raw API status to dashboard column; must match ProgramTable STATUS_COLUMN_MAP + getColumnStatus.
 */
export function adminProgramStatusToColumn(status?: string): ColumnKey {
  const normalized = (status || '').toLowerCase();
  const map: Record<string, ColumnKey> = {
    design: 'design',
    designed: 'design',
    published: 'published',
    invited: 'invited',
    actived: 'active',
    active: 'active'
  };
  return map[normalized] || 'design';
}

/** Normalize any backend model string into the taxonomy values (Masterclass | Guided Masterclass | Mentorship). */
export function normalizeProgramModel(type?: string, typeLabel?: string): string {
  const source = (typeLabel || type || '').toLowerCase();
  if (!source) return '';
  if (source.includes('guided') || source.includes('masterclass&mentorship') || source.includes('masterclass & mentorship')) {
    return 'Guided Masterclass';
  }
  if (source.includes('mentorship')) return 'Mentorship';
  if (source.includes('masterclass') || source.includes('course')) return 'Masterclass';
  return typeLabel || type || '';
}

function programModelFromType(type?: string, typeLabel?: string): string {
  return normalizeProgramModel(type, typeLabel) || 'Masterclass';
}

/** Normalize free-form org type into the taxonomy values (Corporate | Higher Ed | Association). */
export function normalizeOrgType(raw?: string): string {
  const t = (raw || '').toLowerCase();
  if (!t) return '';
  if (t.includes('corp') || t.includes('business') || t.includes('company')) return 'Corporate';
  if (t.includes('high') || t.includes('ed') || t.includes('univ') || t.includes('college') || t.includes('acad')) {
    return 'Higher Ed';
  }
  if (t.includes('assoc') || t.includes('nonprofit') || t.includes('non-profit')) return 'Association';
  return raw || '';
}

const capitalize = (s?: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

/**
 * Standard property bag sent with EVERY taxonomy event (tracking spec):
 * Organization Type/Name · Program Model/Name · User Role/Email · Last Login.
 * Reads the Redux store directly so call sites stay one-liners; empty values
 * are skipped. Merge event-specific props on top (they win on key clashes).
 */
export function getAmplitudeBaseProps(): Record<string, unknown> {
  let state: any = {};
  try {
    state = store.getState();
  } catch {
    return {};
  }
  const pd = state.userProfile?.data?.personal_details;
  const sg = state.selectedGroup?.data;
  const org = state.orgDetails?.data;
  const lastLogin = state.userProfile?.data?.lastLogin;

  const props: Record<string, unknown> = {};
  const put = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;
    props[key] = value;
  };

  put(attributeKeys.organizationName, org?.name);
  put(attributeKeys.organizationType, normalizeOrgType(org?.metadata?.orgType || org?.orgType));
  put(attributeKeys.programName, sg?.name);
  put(attributeKeys.programModel, normalizeProgramModel(sg?.model || sg?.programModel));
  put(attributeKeys.userRole, capitalize(sg?.role) || capitalize(pd?.category));
  put(attributeKeys.userEmail, pd?.email);
  if (typeof lastLogin === 'number' && lastLogin > 0) {
    put(attributeKeys.lastLogin, new Date(lastLogin * 1000).toISOString());
  }
  return props;
}

/** Track a taxonomy event with the standard base context + event-specific props (specifics win). */
export function trackWithBaseProps(eventName: string, specifics: Record<string, unknown> = {}): void {
  eventTracking(eventName, { ...getAmplitudeBaseProps(), ...specifics });
}

/**
 * When admin program list changes, fire lifecycle events only on column transitions (persisted in sessionStorage).
 */
export function trackAdminProgramLifecycleEvents(programs: AdminProgramRow[], ctx: AmplitudeOrgContext = {}): void {
  if (!programs.length) return;
  const prev = readStoredColumns();
  const next: Record<string, ColumnKey> = { ...prev };

  programs.forEach((p) => {
    const col = adminProgramStatusToColumn(p.status);
    const last = prev[p.grpId];
    if (last === col) return;

    const eventName = COLUMN_TO_EVENT[col];
    const baseProps: Record<string, unknown> = {
      [attributeKeys.programName]: p.name,
      [attributeKeys.adminProgramStatus]: COLUMN_TO_ADMIN_STATUS[col],
      [attributeKeys.programModel]: programModelFromType(p.type, p.typeLabel),
      grpId: p.grpId
    };
    if (ctx.organizationName) baseProps[attributeKeys.organizationName] = ctx.organizationName;
    if (ctx.organizationType) baseProps[attributeKeys.organizationType] = ctx.organizationType;
    if (ctx.userEmail) baseProps[attributeKeys.userEmail] = ctx.userEmail;
    if (ctx.userRole) baseProps[attributeKeys.userRole] = ctx.userRole;

    // ATC — attach the per-program build time on Published + the terminal columns
    // (Invited/Active) so a single report can show a course's creation effort
    // next to the status it ended up in. Prefer the value frozen at the "Create"
    // click (identical to the one on `Program Created`, so the same program
    // reports the same number on every event); fall back to the running counter
    // for programs created before the freeze shipped — there it stays a partial
    // snapshot on Published and is final from Invited on. Analyze with
    // Max/latest per program, never Sum. In Design is omitted — drafts with
    // near-zero, noisy time.
    const hasFrozenAtc = typeof p.aimdCreateSeconds === 'number' && p.aimdCreateSeconds > 0;
    const atcSeconds = hasFrozenAtc ? p.aimdCreateSeconds : p.aimdBuildSeconds;
    if (
      (col === 'published' || col === 'invited' || col === 'active') &&
      typeof atcSeconds === 'number' &&
      atcSeconds > 0
    ) {
      baseProps[attributeKeys.activeTimeOnCreationSeconds] = atcSeconds;
      baseProps[attributeKeys.activeTimeOnCreationHours] = Math.round((atcSeconds / 3600) * 10) / 10;
    }

    eventTracking(eventName, baseProps);
    next[p.grpId] = col;
  });

  writeStoredColumns(next);
}

const PROGRAM_CREATED_STORAGE_PREFIX = 'amp_program_created_';

function alreadyTrackedProgramCreated(grpId: string): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    return sessionStorage.getItem(`${PROGRAM_CREATED_STORAGE_PREFIX}${grpId}`) === '1';
  } catch {
    return false;
  }
}

function markProgramCreatedTracked(grpId: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(`${PROGRAM_CREATED_STORAGE_PREFIX}${grpId}`, '1');
  } catch {
    /* ignore */
  }
}

export type ProgramCreatedParams = {
  grpId: string;
  programName: string;
  /** Taxonomy value: Masterclass | Guided Masterclass | Mentorship. */
  programModel?: string;
  /** Taxonomy value: AI MD | Manual | Template. */
  creationFlow?: string;
  organizationName?: string;
};

/**
 * `Program Created` — the one row per course creation that the ATC report reads.
 * Call it from the "Create" click that ends the creation wizard, right after the
 * program is persisted.
 *
 * It freezes the build timer on the BE first (flushing the seconds the 30s
 * heartbeat hasn't sent), so the reported time ends exactly at that click and
 * never grows with later edits. Carries course name, admin email (from the
 * Redux profile via the base props), program status and the final time.
 *
 * Deduped per program per session so a re-publish doesn't add a second row; the
 * BE freeze is write-once, so even a missed dedupe reports the same number.
 */
export async function trackProgramCreated(params: ProgramCreatedParams): Promise<void> {
  const { grpId, programName, programModel, creationFlow, organizationName } = params;
  if (!grpId || alreadyTrackedProgramCreated(grpId)) return;
  markProgramCreatedTracked(grpId);

  const seconds = await finalizeAimdBuildTime(grpId);

  const specifics: Record<string, unknown> = {
    [attributeKeys.programName]: programName,
    [attributeKeys.adminProgramStatus]: 'Published',
    // Whoever clicks Create is acting as an admin, whatever role the currently
    // selected group gives them in the base props.
    [attributeKeys.userRole]: 'Admin',
    grpId
  };
  if (programModel) specifics[attributeKeys.programModel] = programModel;
  if (creationFlow) specifics[attributeKeys.programCreationFlow] = creationFlow;
  if (organizationName) specifics[attributeKeys.organizationName] = organizationName;
  if (typeof seconds === 'number' && seconds > 0) {
    specifics[attributeKeys.activeTimeOnCreationSeconds] = seconds;
    specifics[attributeKeys.activeTimeOnCreationHours] = Math.round((seconds / 3600) * 10) / 10;
  }

  trackWithBaseProps(events.programCreated, specifics);
}

/** Deduped User Onboarded — Redux listener when FTUE API completes (session-scoped dedupe). */
export function trackUserOnboardedFromReduxState(state: OnboardingReduxSnapshot): void {
  const uid = state.userProfile.data?._id;
  if (!uid) return;
  const sk = `amp_user_onboarded_${uid}`;
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(sk)) return;
  try {
    sessionStorage.setItem(sk, '1');
  } catch {
    /* ignore */
  }

  const pd = state.userProfile.data?.personal_details;
  const sg = state.selectedGroup.data;
  const org = state.orgDetails.data;

  eventTracking(events.userOnboarded, {
    [attributeKeys.userEmail]: pd?.email || '',
    [attributeKeys.organizationName]: org?.name || '',
    [attributeKeys.organizationType]: org?.metadata?.orgType || '',
    [attributeKeys.programName]: sg?.name || '',
    [attributeKeys.userRole]: sg?.role ? sg.role.charAt(0).toUpperCase() + sg.role.slice(1) : pd?.category || '',
    [attributeKeys.onboardedDate]: new Date().toISOString()
  });
}
